import React, { useState, useEffect } from 'react';
import './EnhancedPaymentForm.css';

const EnhancedPaymentForm = ({ api, setPaymentResult, token }) => {
    const [formData, setFormData] = useState({
        nameOnCard: '',
        cardNumber: '',
        cardExpiry: '',
        securityNumber: ''
    });

    const [billingAddress, setBillingAddress] = useState({
        postcode: '',
        address1: '',
        address2: '',
        town: '',
        county: ''
    });

    const [addressList, setAddressList] = useState([]);
    const [addressLookupError, setAddressLookupError] = useState('');

    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [isFormValid, setIsFormValid] = useState(false);

    const luhnCheck = (cardNumber) => {
        const digits = cardNumber.replace(/\D/g, '').split('').map(Number);
        let sum = 0;
        let isEven = false;
        
        for (let i = digits.length - 1; i >= 0; i--) {
            let digit = digits[i];
            
            if (isEven) {
                digit *= 2;
                if (digit > 9) {
                    digit -= 9;
                }
            }
            
            sum += digit;
            isEven = !isEven;
        }
        
        return sum % 10 === 0;
    };

    const validateCardNumber = (value) => {
        const cleaned = value.replace(/\s/g, '');
        if (!/^\d{13,19}$/.test(cleaned)) {
            return { isValid: false, errorType: 'length' };
        }
        if (!luhnCheck(cleaned)) {
            return { isValid: false, errorType: 'invalid' };
        }
        return { isValid: true };
    };

    const validateExpiryDate = (expiry) => {
      if (!expiry || expiry.length < 5) return false;

      const [month, year] = expiry.split('/');
      if (!month || !year) return false;

      const expMonth = parseInt(month, 10);
      const expYear = parseInt(year, 10);

      if (expMonth < 1 || expMonth > 12) return false;

      const fullYear = expYear < 100 ? 2000 + expYear : expYear;

      // Card expires at the end of the expiry month
      const expiryDate = new Date(fullYear, expMonth, 0); 

      //removed grace period logic as per new requirements, card must be valid for at least 30 days from current date
      
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

      return expiryDate >= thirtyDaysFromNow;
  };

    const validatePostcode = (postcode) => {
        // UK postcode regex pattern
        const postcodeRegex = /^([A-Z]{1,2}\d[A-Z\d]?)\s?(\d[A-Z]{2})$/i;
        return postcodeRegex.test(postcode.trim());
    };

    const validateField = (name, value) => {
        const newErrors = { ...errors };

        switch (name) {
            case 'nameOnCard':
                if (!value.trim()) {
                    newErrors[name] = 'Cardholder name is required';
                } else if (value.length > 27) {
                    newErrors[name] = 'Name cannot exceed 27 characters';
                } else if (!/^[-a-zA-Z'. ]*$/.test(value)) {
                    newErrors[name] = 'Name can only contain letters, spaces, hyphens, apostrophes, and periods';
                } else {
                    delete newErrors[name];
                }
                break;
            
            case 'cardNumber':
                if (!value) {
                    newErrors[name] = 'Card number is required';
                } else {
                    const validation = validateCardNumber(value);
                    if (!validation.isValid) {
                        if (validation.errorType === 'length') {
                            newErrors[name] = 'Card number must be 13-19 digits';
                        } else if (validation.errorType === 'invalid') {
                            newErrors[name] = "This doesn't appear to be a valid card number";
                        }
                    } else {
                        delete newErrors[name];
                    }
                }
                break;
            
            case 'cardExpiry':
                if (!value) {
                    newErrors[name] = 'Expiry date is required';
                } else if (!validateExpiryDate(value)) {
                    newErrors[name] = 'Card must be valid for at least 30 days';
                } else {
                    delete newErrors[name];
                }
                break;
            
            case 'securityNumber':
                if (!value) {
                    newErrors[name] = 'Security code is required';
                } else if (!/^\d{3,4}$/.test(value)) {
                    newErrors[name] = 'Security code must be 3 or 4 digits';
                } else {
                    delete newErrors[name];
                }
                break;

            // Billing Address validations
            case 'postcode':
                if (!value.trim()) {
                    newErrors[name] = 'Postcode is required';
                } else if (!validatePostcode(value)) {
                    newErrors[name] = 'Please enter a valid UK postcode';
                } else {
                    delete newErrors[name];
                }
                break;

            case 'address1':
                if (!value.trim()) {
                    newErrors[name] = 'Address line 1 is required';
                } else if (value.length > 50) {
                    newErrors[name] = 'Address line 1 cannot exceed 50 characters';
                } else {
                    delete newErrors[name];
                }
                break;

            case 'address2':
                if (value.length > 50) {
                    newErrors[name] = 'Address line 2 cannot exceed 50 characters';
                } else {
                    delete newErrors[name];
                }
                break;

            case 'town':
                if (!value.trim()) {
                    newErrors[name] = 'Town is required';
                } else if (value.length > 30) {
                    newErrors[name] = 'Town cannot exceed 30 characters';
                } else {
                    delete newErrors[name];
                }
                break;

            case 'county':
                if (value.length > 30) {
                    newErrors[name] = 'County cannot exceed 30 characters';
                } else {
                    delete newErrors[name];
                }
                break;
            
            default:
                break;
        }

        setErrors(newErrors);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;

        // Process value based on field type
        if (name === 'nameOnCard') {
            processedValue = value.toUpperCase();
        } else if (name === 'cardNumber') {
            // Remove all non-digits and add spaces every 4 digits
            const digitsOnly = value.replace(/\D/g, '');
            processedValue = digitsOnly.replace(/(\d{4})(?=\d)/g, '$1 ');
            if (processedValue.length > 23) { // 19 digits + 4 spaces = 23 chars max
                processedValue = processedValue.slice(0, 23);
            }
        } else if (name === 'cardExpiry') {
            // Format as MM/YY
            const digitsOnly = value.replace(/\D/g, '');
            if (digitsOnly.length >= 2) {
                processedValue = digitsOnly.slice(0, 2) + '/' + digitsOnly.slice(2, 4);
            } else {
                processedValue = digitsOnly;
            }
        } else if (name === 'securityNumber') {
            processedValue = value.replace(/\D/g, ''); // Only digits
            if (processedValue.length > 4) {
                processedValue = processedValue.slice(0, 4);
            }
        }

        // Handle form data vs billing address data
        if (['postcode', 'address1', 'address2', 'town', 'county'].includes(name)) {
            setBillingAddress(prev => ({
                ...prev,
                [name]: processedValue
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: processedValue
            }));
        }

        // Only validate on blur, not on change
        if (touched[name]) {
            validateField(name, processedValue);
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        setTouched(prev => ({ ...prev, [name]: true }));
        validateField(name, value);
    };

    const handleAddressLookup = async (e) => {
        e.preventDefault();
        setAddressLookupError('');
        
        if (!billingAddress.postcode) {
            setAddressLookupError('Please enter a postcode');
            return;
        }

        try {
            const result = await api.transact('AddressLookup', {
                Postcode: billingAddress.postcode
            });
            setAddressList(result.apiResult || []);
        } catch (error) {
            setAddressLookupError(error.apiText || 'Address lookup failed');
            setAddressList([]);
        }
    };

    const handleAddressSelect = (e) => {
        const index = parseInt(e.target.value, 10);
        if (isNaN(index) || !addressList[index]) return;
        
        const selectedAddress = addressList[index];
        setBillingAddress(prev => ({
            ...prev,
            address1: selectedAddress.Address1 || '',
            address2: selectedAddress.Address2 || '',
            town: selectedAddress.Town || '',
            county: selectedAddress.County || ''
        }));
    };

    useEffect(() => {
        const requiredPaymentFields = ['nameOnCard', 'cardNumber', 'cardExpiry', 'securityNumber'];
        const requiredBillingFields = ['postcode', 'address1', 'town'];
        
        const allPaymentFieldsFilled = requiredPaymentFields.every(field => formData[field]);
        const allBillingFieldsFilled = requiredBillingFields.every(field => billingAddress[field]);
        const noErrors = Object.keys(errors).length === 0;
        
        setIsFormValid(allPaymentFieldsFilled && allBillingFieldsFilled && noErrors);
    }, [formData, billingAddress, errors]);

    const makePayment = async () => {
        try {
            const cleanCardNumber = formData.cardNumber.replace(/\s/g, '');
            const [expMonth, expYear] = formData.cardExpiry.split('/');
            
            const params = {
                Token: token,
                CardNo: cleanCardNumber,
                ExpiryDate: {
                    Month: expMonth,
                    Year: expYear
                },
                CV2: formData.securityNumber,
                CardHolderName: formData.nameOnCard,
                Address: {
                    Address1: billingAddress.address1,
                    Address2: billingAddress.address2,
                    Town: billingAddress.town,
                    County: billingAddress.county,
                    Postcode: billingAddress.postcode
                }
            };

            const result = await api.transact("MakePortalPayment", params);
            
            if (result.apiStatus === "OK") {
                setPaymentResult('Success');
            } else {
                setPaymentResult('Unsuccessful');
            }
        } catch (error) {
            setPaymentResult('Error');
        }
    };

    return (
        <form>
            <div className="form__group">
                <div className="form__title">
                    <h2>Payment details</h2>
                </div>
                
                <div className="form__field">
                    <label htmlFor="nameOnCard">Name on Card</label>
                    <div className="form__input__wrapper">
                        <input
                            type="text"
                            id="nameOnCard"
                            name="nameOnCard"
                            className={`form__input ${errors.nameOnCard ? 'form__input--error' : ''}`}
                            value={formData.nameOnCard}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Name as it appears on card"
                            maxLength="27"
                            pattern="[-a-zA-Z'. ]*"
                            autoComplete="cc-name"
                            tabIndex={1}
                        />
                        <span className={`form__error ${errors.nameOnCard ? 'form__error--visible' : ''}`}>
                            {errors.nameOnCard || '\u00A0'}
                        </span>
                    </div>
                </div>

                <div className="form__field">
                    <label htmlFor="cardNumber">Card Number</label>
                    <div className="form__input__wrapper">
                        <input
                            type="text"
                            id="cardNumber"
                            name="cardNumber"
                            className={`form__input ${errors.cardNumber ? 'form__input--error' : ''}`}
                            value={formData.cardNumber}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="XXXX XXXX XXXX XXXX"
                            maxLength="23"
                            pattern="[0-9\s]*"
                            inputMode="numeric"
                            autoComplete="cc-number"
                            tabIndex={2}
                        />
                        <span className={`form__error ${errors.cardNumber ? 'form__error--visible' : ''}`}>
                            {errors.cardNumber || '\u00A0'}
                        </span>
                    </div>
                </div>

                <div className="form__field">
                    <label htmlFor="cardExpiry">Expiry Date</label>
                    <div className="form__input__wrapper">
                        <input
                            type="text"
                            id="cardExpiry"
                            name="cardExpiry"
                            className={`form__input ${errors.cardExpiry ? 'form__input--error' : ''}`}
                            value={formData.cardExpiry}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="MM/YY"
                            maxLength="5"
                            pattern="[0-9/]*"
                            inputMode="numeric"
                            autoComplete="cc-exp"
                            tabIndex={3}
                        />
                        <span className={`form__error ${errors.cardExpiry ? 'form__error--visible' : ''}`}>
                            {errors.cardExpiry || '\u00A0'}
                        </span>
                    </div>
                </div>

                <div className="form__field">
                    <label htmlFor="securityNumber">Security number</label>
                    <div className="form__input__wrapper">
                        <input
                            type="text"
                            id="securityNumber"
                            name="securityNumber"
                            className={`form__input ${errors.securityNumber ? 'form__input--error' : ''}`}
                            value={formData.securityNumber}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="XXX"
                            maxLength="4"
                            pattern="[0-9]*"
                            inputMode="numeric"
                            autoComplete="cc-csc"
                            tabIndex={4}
                        />
                        <span className={`form__error ${errors.securityNumber ? 'form__error--visible' : ''}`}>
                            {errors.securityNumber || '\u00A0'}
                        </span>
                    </div>
                </div>

                <br />

                <div className="form__info">
                    <p>£1 will be taken from your card to authenticate your card.</p>
                    <p>This will be automatically refunded within 5 days.</p>
                </div>
                
                <hr />
           


            {/* Billing Address Section */}
        
                <div className="form__title">
                    <h2>Billing Address</h2>
                </div>

                <div className="form__field">
                    <label htmlFor="postcode">Postcode</label>
                    <div className="postcode-container">
                        <input
                            type="text"
                            id="postcode"
                            name="postcode"
                            className={`form__input ${errors.postcode ? 'form__input--error' : ''}`}
                            value={billingAddress.postcode}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="e.g. SW1A 1AA"
                            maxLength="8"
                            autoComplete="billing postal-code"
                            tabIndex={5}
                        />
                        <button
                            type="button"
                            onClick={handleAddressLookup}
                            tabIndex={6}
                        >
                            Find Address
                        </button>
                    </div>
                    <span className={`form__error ${errors.postcode ? 'form__error--visible' : ''}`}>
                        {errors.postcode || '\u00A0'}
                    </span>
                </div>

                {addressLookupError && (
                    <div className="form__field">
                        <label></label>
                        <div className="form__input__wrapper">
                            <div className="address-lookup-error">
                                Find Address Error: {addressLookupError}
                            </div>
                        </div>
                    </div>
                )}

                {addressList.length > 0 && (
                    <div className="form__field">
                        <label htmlFor="addressSelect">Select Address</label>
                        <div className="form__input__wrapper">
                            <select
                                id="addressSelect"
                                onChange={handleAddressSelect}
                                className="form__input"
                                tabIndex={7}
                            >
                                    <option value="">Please select an address</option>
                                    {addressList.map((address, index) => (
                                        <option key={`${address.Postcode}-${index}`} value={index}>
                                            {`${address.Address1} ${address.Address2} ${address.Town} ${address.County} ${address.Postcode}`}
                                        </option>
                                    ))}
                            </select>
                        </div>
                    </div>
                )}

                <div className="form__field">
                    <label htmlFor="address1">1st Line of Address</label>
                    <div className="form__input__wrapper">
                        <input
                            type="text"
                            id="address1"
                            name="address1"
                            className={`form__input ${errors.address1 ? 'form__input--error' : ''}`}
                            value={billingAddress.address1}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="House number and street name"
                            maxLength="50"
                            autoComplete="billing address-line1"
                            tabIndex={8}
                        />
                        <span className={`form__error ${errors.address1 ? 'form__error--visible' : ''}`}>
                            {errors.address1 || '\u00A0'}
                        </span>
                    </div>
                </div>

                <div className="form__field">
                    <label htmlFor="address2">2nd Line of Address</label>
                    <div className="form__input__wrapper">
                        <input
                            type="text"
                            id="address2"
                            name="address2"
                            className={`form__input ${errors.address2 ? 'form__input--error' : ''}`}
                            value={billingAddress.address2}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Apartment, suite, etc. (optional)"
                            maxLength="50"
                            autoComplete="billing address-line2"
                            tabIndex={9}
                        />
                        <span className={`form__error ${errors.address2 ? 'form__error--visible' : ''}`}>
                            {errors.address2 || '\u00A0'}
                        </span>
                    </div>
                </div>

                <div className="form__field">
                    <label htmlFor="town">Town</label>
                    <div className="form__input__wrapper">
                        <input
                            type="text"
                            id="town"
                            name="town"
                            className={`form__input ${errors.town ? 'form__input--error' : ''}`}
                            value={billingAddress.town}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Town or city"
                            maxLength="30"
                            autoComplete="billing address-level2"
                            tabIndex={10}
                        />
                        <span className={`form__error ${errors.town ? 'form__error--visible' : ''}`}>
                            {errors.town || '\u00A0'}
                        </span>
                    </div>
                </div>

                <div className="form__field">
                    <label htmlFor="county">County</label>
                    <div className="form__input__wrapper">
                        <input
                            type="text"
                            id="county"
                            name="county"
                            className={`form__input ${errors.county ? 'form__input--error' : ''}`}
                            value={billingAddress.county}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="County (optional)"
                            maxLength="30"
                            autoComplete="billing address-level1"
                            tabIndex={11}
                        />
                        <span className={`form__error ${errors.county ? 'form__error--visible' : ''}`}>
                            {errors.county || '\u00A0'}
                        </span>
                    </div>
                </div>

                <div className="form__info">
                    Your personal information provided to us will be used for purposes of
                    booking your appointment.<br />
                    To find out more about our promise to
                    protecting your personal information please see our&nbsp;
                    <span>
                        <a href="https://www.iprshealth.com/about-us/gdpr/patientprivacynotice/"
                        target='_blank'>
                        Privacy Policy</a>
                    </span>
                </div>
            </div>

            {isFormValid ? (
                <p className="form-status-ready">Ready to process payment.</p>
            ) : (
                <p className="form-status-incomplete">Please complete all fields.</p>
            )}

            <div className="form__button__center">
                <button
                    type="button"
                    className="form__action form-action-spaced"
                    onClick={makePayment}
                    disabled={!isFormValid}
                    tabIndex={12}
                >
                    Confirm
                </button>
            </div>
        </form>
    );
};

export default EnhancedPaymentForm;